import * as Sentry from '@sentry/react'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { BrowserRouter } from 'react-router-dom'
import './App.css'
import AppContent, { NavyScreen, isPublicPath } from './AppContent'
import { hasStoredUser } from './api/authApi'
import Loading from './Components/Layers/Loading.tsx'
import ErrorFallback from './Components/shared/ErrorFallback.tsx'
import MaintenancePage from './Components/shared/MaintenancePage.tsx'
import { AuthProvider } from './Contexts/AuthContext'
import { CacheProvider } from './Contexts/CacheContext'
import { GisUploadProvider } from './Contexts/GisUploadContext'
import { LidarUploadProvider } from './Contexts/LidarUploadContext'
import { MapStyleProvider } from './Contexts/MapStyleContext.tsx'
import { ModalProvider } from './Contexts/ModalContext'
import { ThemeProvider } from './Contexts/ThemeContext'
import { BACKEND_RECOVERED_EVENT, BACKEND_UNAVAILABLE_EVENT } from './api/api'
import { useMapboxAttribution } from './hooks/useMapboxAttribution.ts'
import PublicShareApp from './routes/PublicShareApp'
import { isPublicSharePath, redactSentryEvent } from './utils/redactShareToken'

// Sur le lien public, l URL porte le token d acces. Le replay capture les URL HORS de
// beforeSend : le seul recours est de ne pas monter l integration sur ce chemin.
const onPublicShare = isPublicSharePath(window.location.pathname)

// Initialize Sentry with current v8 syntax (only once)
if (!window.__SENTRY_INITIALIZED__) {
  Sentry.init({
    dsn: 'https://c6efa11a085ff1333d8433ba3cba77af@o4509389523582976.ingest.de.sentry.io/4509389525155920',
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(onPublicShare
        ? []
        : [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ]),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Setting this option to true will send default PII data to Sentry
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,
    // URL de page, nom de transaction et fils d Ariane (navigation + fetch) portent tous
    // le token : on masque, on ne supprime pas.
    beforeSend: redactSentryEvent,
    beforeSendTransaction: redactSentryEvent,
  })
  window.__SENTRY_INITIALIZED__ = true
}

function App() {
  useMapboxAttribution()

  const isDevelopment = import.meta.env.DEV
  // null = unknown (probing), true = maintenance, false = ready
  const [isMaintenance, setIsMaintenance] = useState(null)
  const retryIntervalRef = useRef(null)

  const probeBackend = useCallback(async () => {
    const baseUrl = import.meta.env.VITE_URL
    if (!baseUrl) {
      setIsMaintenance(false)
      return
    }

    const base = String(baseUrl).replace(/\/$/, '')
    const probeUrl = `${base}/readyz`

    const maintenanceStatuses = [503, 502, 504]

    try {
      const readyRes = await fetch(probeUrl, {
        method: 'GET',
        credentials: 'include',
      })

      if (maintenanceStatuses.includes(readyRes.status)) {
        setIsMaintenance(true)
        return
      }

      if (readyRes.ok) {
        setIsMaintenance(false)
        return
      }

      if (readyRes.status === 401 || readyRes.status === 403) {
        try {
          const meRes = await fetch(`${base}/users/me`, {
            method: 'GET',
            credentials: 'include',
          })
          if (maintenanceStatuses.includes(meRes.status)) {
            setIsMaintenance(true)
            return
          }
        } catch {
          setIsMaintenance(isDevelopment ? false : true)
          return
        }

        setIsMaintenance(false)
        return
      }

      setIsMaintenance(isDevelopment ? false : true)
    } catch {
      setIsMaintenance(isDevelopment ? false : true)
    }
  }, [isDevelopment])

  useEffect(() => {
    // Basculer demonte toute l appli : une requete coupee par un proxy client ne suffit pas.
    const onUnavailable = () => probeBackend()
    const onRecovered = () => setIsMaintenance(false)

    window.addEventListener(BACKEND_UNAVAILABLE_EVENT, onUnavailable)
    window.addEventListener(BACKEND_RECOVERED_EVENT, onRecovered)

    return () => {
      window.removeEventListener(BACKEND_UNAVAILABLE_EVENT, onUnavailable)
      window.removeEventListener(BACKEND_RECOVERED_EVENT, onRecovered)
    }
  }, [probeBackend])

  useEffect(() => {
    // first probe on mount, then keep probing only when in maintenance.
    if (isMaintenance === null) {
      probeBackend()
      return
    }
    if (isMaintenance === false) return

    probeBackend()
    retryIntervalRef.current = window.setInterval(() => {
      probeBackend()
    }, 15000)

    return () => {
      if (retryIntervalRef.current) {
        window.clearInterval(retryIntervalRef.current)
        retryIntervalRef.current = null
      }
    }
  }, [isMaintenance, probeBackend])

  // Public share route — minimal providers, no auth
  const isPublicShare = isPublicSharePath(window.location.pathname)
  if (isPublicShare) {
    return <PublicShareApp />
  }

  return (
    <ThemeProvider>
      <Sentry.ErrorBoundary
        fallback={
          isDevelopment
            ? ({ error, resetError }) => (
                <ErrorFallback error={error} resetError={resetError} />
              )
            : undefined
        }
      >
        <div className="App">
          {isMaintenance === true ? <MaintenancePage /> : null}
          {isMaintenance === null &&
          (isPublicPath(window.location.pathname) || !hasStoredUser()) ? (
            <NavyScreen />
          ) : null}
          <Suspense fallback={<Loading />}>
            {isMaintenance === false ? (
              <BrowserRouter>
                <MapStyleProvider>
                  <ModalProvider>
                    <AuthProvider>
                      <CacheProvider>
                        <LidarUploadProvider>
                        <GisUploadProvider>
                        <AppContent />
                        </GisUploadProvider>
                        </LidarUploadProvider>
                    </CacheProvider>
                    </AuthProvider>
                  </ModalProvider>
                </MapStyleProvider>
              </BrowserRouter>
            ) : null}
          </Suspense>
        </div>
      </Sentry.ErrorBoundary>
    </ThemeProvider>
  )
}

export default App
